import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  type ReportReason,
  ReportReasonSchema,
  type ReportStatus,
  ReportStatusSchema,
} from '@q4udoc/types';
import { useState } from 'react';
import {
  Button,
  Confirm,
  DatagridConfigurable,
  DateField,
  FunctionField,
  List,
  SelectField,
  SelectInput,
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { ReasonChip, StatusChip } from '../components/ChoiceChips';
import { ListActions } from '../components/ListActions';
import { commonListSx } from '../components/listStyles';
import { ScrollableDatagrid } from '../components/ScrollableDatagrid';
import { TruncatedTextField } from '../components/TruncatedTextField';
import { useResourceAction } from '../hooks/useResourceAction';

// Question Reports: user-filed reports against questions. From a report's Show
// page an admin can Dismiss it (no change to the question) or Remove the
// question — a moderation soft-delete (status → 'deleted') that drops it from
// feeds/trending/new quizzes while every reference to it stays intact.

// Ids come from the shared enums so a new status or reason cannot be missed here; the human
// labels stay local and are the single source for the list chips and the Show page alike.
const STATUS_LABELS: Record<ReportStatus, string> = {
  pending: 'Pending',
  dismissed: 'Dismissed',
  actioned: 'Actioned (removed)',
};
const STATUS_CHOICES = ReportStatusSchema.options.map((id) => ({ id, name: STATUS_LABELS[id] }));

const REASON_LABELS: Record<ReportReason, string> = {
  incorrect_answer: 'Incorrect Answer',
  outdated_information: 'Outdated Information',
  unclear_question: 'Unclear Question',
  inappropriate_content: 'Inappropriate Content',
  duplicate: 'Duplicate',
  other: 'Other',
};
const REASON_CHOICES = ReportReasonSchema.options.map((id) => ({ id, name: REASON_LABELS[id] }));

// The questions resource is no longer part of the admin panel, so the report's
// questionId is shown as a plain reference (use it to look the question up in
// Firestore / the pipeline) rather than a link to a route that no longer exists.
const QuestionIdField = () => {
  const record = useRecordContext();
  if (!record?.questionId) return null;

  return (
    <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
      {record.questionId as string}
    </span>
  );
};

const QuestionReportFilters = [
  <SelectInput key="status" source="status" label="Status" choices={STATUS_CHOICES} alwaysOn />,
];

export const QuestionReportList = () => (
  <List
    filters={QuestionReportFilters}
    actions={<ListActions />}
    sx={commonListSx}
    sort={{ field: 'createdAt', order: 'DESC' }}
  >
    <ScrollableDatagrid>
      <DatagridConfigurable rowClick="show" bulkActionButtons={false}>
        <FunctionField
          label="Status"
          render={() => (
            <StatusChip
              choices={STATUS_CHOICES}
              colors={{ pending: 'warning', actioned: 'success', dismissed: 'default' }}
            />
          )}
        />
        <FunctionField label="Reason" render={() => <ReasonChip choices={REASON_CHOICES} />} />
        <FunctionField label="Question ID" render={() => <QuestionIdField />} />
        <FunctionField
          label="Question"
          render={() => <TruncatedTextField source="questionText" maxLength={60} />}
        />
        <TextField source="reporterDisplayName" label="Reporter" />
        <FunctionField
          label="Description"
          render={() => <TruncatedTextField source="description" maxLength={40} />}
        />
        <DateField source="createdAt" label="Reported" showTime />
      </DatagridConfigurable>
    </ScrollableDatagrid>
  </List>
);

const DismissButton = () => {
  const record = useRecordContext();
  const dismiss = useResourceAction('questionReports', 'dismiss', {
    success: 'Report dismissed',
    error: 'Error dismissing report',
  });
  if (record?.status !== 'pending') return null;
  return <Button label="Dismiss" onClick={() => dismiss()} startIcon={<CheckIcon />} />;
};

const RemoveQuestionButton = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const remove = useResourceAction('questionReports', 'remove', {
    success: 'Question removed',
    error: 'Error removing question',
  });
  if (record?.status !== 'pending') return null;

  return (
    <>
      <Button
        label="Remove Question"
        onClick={() => setOpen(true)}
        startIcon={<DeleteIcon />}
        color="error"
      />
      <Confirm
        isOpen={open}
        fullWidth
        title="Remove this question?"
        content="The question is soft-deleted — hidden from feeds, trending, and new quizzes. Existing saved quizzes keep resolving it. This cannot be undone from the panel."
        confirm="Remove"
        confirmColor="warning"
        onConfirm={async () => {
          if (await remove()) setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const QuestionReportShowActions = () => (
  <TopToolbar>
    <DismissButton />
    <RemoveQuestionButton />
  </TopToolbar>
);

export const QuestionReportShow = () => (
  <Show actions={<QuestionReportShowActions />}>
    <SimpleShowLayout>
      <TextField source="questionText" label="Question" />
      <SelectField source="status" choices={STATUS_CHOICES} />
      <SelectField source="reason" choices={REASON_CHOICES} />
      <TextField source="description" label="User Description" />
      <TextField source="reporterDisplayName" label="Reporter" />
      <TextField source="reporterEmail" label="Reporter Email" />
      <DateField source="createdAt" label="Reported At" showTime />
      <DateField source="resolvedAt" label="Resolved At" showTime />
      <TextField source="resolvedBy" label="Resolved By" />
      <FunctionField label="Question ID" render={() => <QuestionIdField />} />
    </SimpleShowLayout>
  </Show>
);
